/*
 * Purpose: Intent → OutcomePlan — maps user goals to blocks, connectors, APIs
 * Last modified: 2026-06-25 | Agent
 * Completeness: 82
 */
import { AVERAGE_USER_TEMPLATES } from '@/services/averageUserTemplates';
import { findApisForText, findConnectorsForText, CONNECTOR_CATALOG } from './connectorCatalog';
import type {
  ApiRecommendation,
  ConnectorRequirement,
  CustomBlockDefinition,
  OutcomePlan,
  PlannedStep,
} from './types';

const genId = () => `plan-${Date.now().toString(36)}`;

interface IntentPattern {
  id: string;
  templateKey?: keyof typeof AVERAGE_USER_TEMPLATES;
  keywords: string[];
  goal: string;
  trigger: 'webhook' | 'manual';
  buildCustom?: (outcome: string) => { steps: PlannedStep[]; customBlocks: CustomBlockDefinition[]; fixture: Record<string, unknown> };
}

const INTENT_PATTERNS: IntentPattern[] = [
  {
    id: 'linkedin_job_hunt',
    templateKey: 'linkedin_job_hunt_pipeline',
    keywords: ['linkedin', 'job', 'resume', 'cover letter', 'apply', 'hiring', 'job hunt', 'job alert'],
    goal: 'Automate LinkedIn job alerts through relevance check, tailoring, apply, track, follow-up, and outreach.',
    trigger: 'webhook',
  },
  {
    id: 'job_tracker',
    templateKey: 'job_application_tracker',
    keywords: ['track application', 'application tracker', 'log application', 'log every job', 'job tracker', 'spreadsheet'],
    goal: 'Log each job application with company, role, date, and status.',
    trigger: 'manual',
  },
  {
    id: 'job_followup',
    templateKey: 'job_followup_reminder',
    keywords: ['follow up', 'follow-up', 'reminder', 'no response'],
    goal: 'Remind you to follow up on applications after a set number of days.',
    trigger: 'manual',
  },
  {
    id: 'receipt_expense',
    templateKey: 'receipt_email_to_tracker',
    keywords: ['receipt', 'expense', 'purchase', 'invoice'],
    goal: 'Parse receipt emails and log amounts to an expense tracker.',
    trigger: 'webhook',
  },
  {
    id: 'appointment_reminder',
    templateKey: 'appointment_reminder',
    keywords: ['appointment', 'booking', 'calendar', 'remind', '24 hour'],
    goal: 'Send a reminder 24 hours before an appointment.',
    trigger: 'webhook',
  },
  {
    id: 'generic_notify',
    keywords: ['notify', 'alert', 'when', 'send me'],
    goal: 'Watch for an event and notify you when it happens.',
    trigger: 'webhook',
    buildCustom: () => ({
      steps: [
        step('s1', 'trigger.webhook', 'Event received', 'trigger', 'webhook', 'Inbound event starts the flow.', {
          name: 'Inbound event',
          url: 'https://your-automation.com/webhook/event',
        }),
        step('s2', 'tool.transform', 'Normalize payload', 'tool', 'transform', 'Shape raw event data.', {
          name: 'Normalize',
          transform: 'return { ...input, normalizedAt: new Date().toISOString() };',
        }),
        step('s3', 'action.notification', 'Notify user', 'action', 'notification', 'Tell the user something happened.', {
          message: 'Event received and processed.',
        }),
      ],
      customBlocks: [],
      fixture: { event: 'sample', value: 'demo' },
    }),
  },
];

function step(
  id: string,
  blockId: string,
  label: string,
  type: PlannedStep['type'],
  subtype: string,
  rationale: string,
  config: Record<string, unknown>
): PlannedStep {
  return { id, blockId, label, type, subtype, rationale, config };
}

function scoreIntent(outcome: string, pattern: IntentPattern): number {
  const lower = outcome.toLowerCase();
  let score = 0;
  for (const kw of pattern.keywords) {
    if (lower.includes(kw)) score += kw.split(' ').length > 1 ? 3 : 1;
  }
  return score;
}

function templateToSteps(templateKey: keyof typeof AVERAGE_USER_TEMPLATES): PlannedStep[] {
  const tpl = AVERAGE_USER_TEMPLATES[templateKey];
  return tpl.nodes.map((node, i) => ({
    id: `step-${i}`,
    blockId: `${node.type}.${node.subtype}`,
    label: String(node.config.name || node.subtype),
    type: node.type,
    subtype: node.subtype,
    rationale: `From proven template: ${tpl.name}`,
    config: { ...node.config },
  }));
}

function connectorsFromText(outcome: string): ConnectorRequirement[] {
  const found = findConnectorsForText(outcome);
  const defaults = outcome.toLowerCase().includes('email') ? [CONNECTOR_CATALOG[0]] : [];

  const merged = [...found];
  for (const d of defaults) {
    if (!merged.some((m) => m.id === d.id)) merged.push(d);
  }

  return merged.map((c) => ({
    id: c.id,
    name: c.name,
    purpose: c.purpose,
    authType: c.authType,
    docsUrl: c.docsUrl,
    setupSteps: c.setupSteps,
    status: c.authType === 'none' || c.authType === 'webhook' ? 'simulated' : 'needs_credentials',
  }));
}

function apisFromText(outcome: string): ApiRecommendation[] {
  return findApisForText(outcome).map((a) => ({
    id: a.id,
    name: a.name,
    url: a.baseUrl,
    purpose: a.purpose,
    method: a.methods[0] ?? 'GET',
  }));
}

function buildFixtureForIntent(patternId: string): Record<string, unknown> {
  switch (patternId) {
    case 'linkedin_job_hunt':
      return {
        from: 'jobs-noreply@linkedin.com',
        subject: 'New jobs: Senior Engineer',
        roles: [
          { company: 'Acme Corp', title: 'Senior Engineer', url: 'https://linkedin.com/jobs/123', score: 85 },
        ],
      };
    case 'job_tracker':
      return { company: 'Acme Corp', role: 'Senior Engineer', channel: 'linkedin', status: 'applied' };
    case 'job_followup':
      return {
        company: 'Acme Corp',
        role: 'Senior Engineer',
        appliedAt: new Date(Date.now() - 8 * 86400000).toISOString(),
        status: 'applied',
        followUpDue: true,
      };
    case 'receipt_expense':
      return { merchant: 'Coffee Shop', amount: 4.5, currency: 'USD', date: new Date().toISOString() };
    case 'appointment_reminder':
      return {
        title: 'Dental checkup',
        location: 'Main Street Clinic',
        startTime: new Date(Date.now() + 86400000).toISOString(),
      };
    default:
      return { sample: true, triggeredAt: new Date().toISOString() };
  }
}

export function planFromOutcome(rawOutcome: string): OutcomePlan {
  const trimmed = rawOutcome.trim();
  if (!trimmed) {
    throw new Error('Describe the outcome you want — e.g. "When LinkedIn emails me jobs, track applications I make."');
  }

  const ranked = INTENT_PATTERNS.map((p) => ({ pattern: p, score: scoreIntent(trimmed, p) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);

  const best = ranked[0]?.pattern ?? INTENT_PATTERNS.find((p) => p.id === 'generic_notify')!;
  const confidence = ranked[0] ? Math.min(0.95, 0.45 + ranked[0].score * 0.08) : 0.35;

  let steps: PlannedStep[] = [];
  let customBlocks: CustomBlockDefinition[] = [];
  let dryRunFixture = buildFixtureForIntent(best.id);
  let matchedTemplateId: string | undefined;

  if (best.templateKey) {
    matchedTemplateId = best.templateKey;
    steps = templateToSteps(best.templateKey);
    const tpl = AVERAGE_USER_TEMPLATES[best.templateKey];
    dryRunFixture = buildFixtureForIntent(best.id);
    if (best.trigger === 'webhook' && steps[0]?.subtype === 'webhook') {
      steps[0] = {
        ...steps[0],
        rationale: 'Email or external service POSTs parsed JSON here.',
      };
    }
  } else if (best.buildCustom) {
    const built = best.buildCustom(trimmed);
    steps = built.steps;
    customBlocks = built.customBlocks;
    dryRunFixture = built.fixture;
  }

  const connectors = connectorsFromText(trimmed);
  const apis = apisFromText(trimmed);

  const assumptions: string[] = [];
  const gaps: string[] = [];

  if (connectors.some((c) => c.status === 'needs_credentials')) {
    assumptions.push('You will add API keys or OAuth tokens in Secrets before going live.');
  }
  if (best.templateKey === 'linkedin_job_hunt_pipeline') {
    gaps.push('LinkedIn apply automation may require browser automation or official API approval.');
    assumptions.push('Dry run uses sample job email data — not your real inbox.');
  }
  if (!matchedTemplateId && steps.length <= 3) {
    gaps.push('This is a minimal scaffold — you may want more steps after review.');
  }

  // Suggest custom AI block when resume/cover mentioned but no template match detail
  if (/resume|cover letter|tailor/i.test(trimmed) && !customBlocks.some((b) => b.id === 'ai_tailor')) {
    customBlocks.push({
      id: 'ai_tailor',
      name: 'AI resume / cover tailor',
      description: 'Calls OpenAI with fact-only prompts — never invent experience.',
      type: 'tool',
      subtype: 'http',
      rationale: 'User asked for tailoring; OpenAI Chat API is the standard approach.',
      defaultConfig: {
        name: 'Tailor with AI (dry-run safe)',
        url: 'https://api.openai.com/v1/chat/completions',
        method: 'POST',
        headers: '{"Authorization":"Bearer $OPENAI_API_KEY","Content-Type":"application/json"}',
      },
    });
    if (!apis.some((a) => a.id === 'openai_chat')) {
      apis.push({
        id: 'openai_chat',
        name: 'OpenAI Chat Completions',
        url: 'https://api.openai.com/v1/chat/completions',
        purpose: 'Generate tailored resume and cover letter text.',
        method: 'POST',
      });
    }
  }

  return {
    id: genId(),
    rawOutcome: trimmed,
    interpretedGoal: best.goal,
    confidence,
    steps,
    connectors,
    apis,
    customBlocks,
    assumptions,
    gaps,
    dryRunFixture,
    matchedTemplateId,
  };
}
